import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'rsuite';
import {
  attachmentsOf,
  groupBattlefield,
  isTapped,
  LANDS_SECTION_KEY,
  primaryTypeOfInstance,
  stackLands,
  unattachedBattlefieldIds,
} from '../../../shared/playtest';
import CardTile, { tileDimensions } from './CardTile';
import { displayOf } from './playtestDisplay';
import { PlaytestDropZone, useDragSourceProps } from './PlaytestDnD';
import { usePlaytestSessions } from './PlaytestSessionProvider';
import { FRAME_BORDER, FRAME_RADIUS } from './playtestStyles';

const SPACING = { section: 16, row: 8, unit: 4 };
const BATTLEFIELD_MIN_HEIGHT = 140;

const LAND_COUNT_PREFIX = '×';
const EXPAND_LABEL = 'Expand';
const COLLAPSE_LABEL = 'Collapse';

const styles = {
  board: { display: 'flex', flexDirection: 'column', gap: SPACING.row },
  battlefield: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.section,
    minHeight: BATTLEFIELD_MIN_HEIGHT,
    padding: SPACING.row,
    border: FRAME_BORDER,
    borderRadius: FRAME_RADIUS,
  },
  section: { display: 'flex', flexDirection: 'column', gap: SPACING.row },
  landsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.row,
    paddingTop: SPACING.row,
    borderTop: FRAME_BORDER,
  },
  sectionTitle: { margin: 0 },
  cards: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'flex-start' },
  unit: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING.unit },
  attachments: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: SPACING.unit },
};

/** Keeps a turned tile's layout slot the size of its visual footprint. */
const slotStyle = (dimensions, tapped) => ({
  flex: 'none',
  width: tapped ? dimensions.height : dimensions.width,
  height: tapped ? dimensions.width : dimensions.height,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * The battlefield in sections (Lands last). A click turns a card; Details opens
 * its detail panel. Attached cards are drawn under their host, not in sections.
 */
const PlaytestBoard = ({ collectionId, session, onOpenDetail, drops }) => {
  const { dispatchAction } = usePlaytestSessions();
  const dragSourceProps = useDragSourceProps();
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (key) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const state = session.engine;
  const sections = groupBattlefield(
    unattachedBattlefieldIds(state),
    primaryTypeOfInstance(state, session.cardViews)
  );

  const renderCard = (instanceId, compact) => {
    const view = displayOf(session, state.instances[instanceId].cardId);
    const tapped = isTapped(state, instanceId);
    const dimensions = tileDimensions(compact);
    return (
      <div key={instanceId} style={styles.unit}>
        <PlaytestDropZone
          testId={`battlefield-slot-${instanceId}`}
          target={{ kind: 'card', instanceId }}
          drops={drops}
          style={slotStyle(dimensions, tapped)}
        >
          <CardTile
            testId={`battlefield-card-${instanceId}`}
            name={view.name}
            typeLine={view.typeLine}
            imageSmall={view.imageSmall}
            compact={compact}
            tapped={tapped}
            ariaLabel={`${view.name}, ${tapped ? 'tapped' : 'untapped'}`}
            dragProps={dragSourceProps(instanceId)}
            onClick={() =>
              dispatchAction(collectionId, { type: 'setTapped', instanceId, tapped: !tapped })
            }
          />
        </PlaytestDropZone>
        <Button
          size="xs"
          data-testid={`battlefield-details-${instanceId}`}
          onClick={() => onOpenDetail(instanceId)}
        >
          Details
        </Button>
        {compact ? null : renderAttachments(instanceId)}
      </div>
    );
  };

  const renderAttachments = (hostId) => {
    const attached = attachmentsOf(state, hostId);
    if (attached.length === 0) return null;
    return (
      <div data-testid={`battlefield-attachments-${hostId}`} style={styles.attachments}>
        {attached.map((instanceId) => renderCard(instanceId, true))}
      </div>
    );
  };

  /**
   * One tile per land stack. A land that hosts attachments stays a card, so its
   * attachments draw under it. Right-click toggles the expanded individual tiles.
   */
  const renderLandStack = (stack) => {
    const hostId = stack.instanceIds[0];
    if (stack.count === 1 && attachmentsOf(state, hostId).length > 0) return renderCard(hostId, false);
    const view = displayOf(session, stack.cardId);
    if (expanded.has(stack.key)) {
      return (
        <div key={stack.key} data-testid={`land-stack-expanded-${stack.key}`} style={styles.section}>
          <Button
            size="xs"
            data-testid={`land-stack-toggle-${stack.key}`}
            aria-expanded="true"
            aria-label={`${COLLAPSE_LABEL} ${view.name}, ${stack.count}`}
            onClick={() => toggleExpanded(stack.key)}
          >
            {COLLAPSE_LABEL}
          </Button>
          <div style={styles.cards}>{stack.instanceIds.map((instanceId) => renderCard(instanceId, false))}</div>
        </div>
      );
    }
    return (
      <div key={stack.key} style={styles.unit}>
        <PlaytestDropZone
          testId={`land-stack-slot-${stack.key}`}
          target={{ kind: 'card', instanceId: hostId }}
          drops={drops}
          style={slotStyle(tileDimensions(false), stack.tapped)}
        >
          <CardTile
            testId={`land-stack-${stack.key}`}
            name={view.name}
            typeLine={view.typeLine}
            imageSmall={view.imageSmall}
            tapped={stack.tapped}
            ariaLabel={`${view.name}, ${stack.count}, ${stack.tapped ? 'tapped' : 'untapped'}`}
            dragProps={dragSourceProps(hostId)}
            onClick={() =>
              dispatchAction(collectionId, { type: 'setTapped', instanceId: hostId, tapped: !stack.tapped })
            }
            onContextMenu={(event) => {
              event.preventDefault();
              toggleExpanded(stack.key);
            }}
          >
            <span>{`${LAND_COUNT_PREFIX}${stack.count}`}</span>
          </CardTile>
        </PlaytestDropZone>
        <Button
          size="xs"
          data-testid={`land-stack-toggle-${stack.key}`}
          aria-expanded="false"
          aria-label={`${EXPAND_LABEL} ${view.name}, ${stack.count}`}
          onClick={() => toggleExpanded(stack.key)}
        >
          {EXPAND_LABEL}
        </Button>
      </div>
    );
  };

  const renderSection = (section) => (
    <section
      key={section.key}
      data-testid={`battlefield-section-${section.key}`}
      style={section.key === LANDS_SECTION_KEY ? styles.landsSection : styles.section}
    >
      <h5 style={styles.sectionTitle}>{section.label}</h5>
      <div style={styles.cards}>
        {section.key === LANDS_SECTION_KEY
          ? stackLands(state, section.ids).map(renderLandStack)
          : section.ids.map((instanceId) => renderCard(instanceId, false))}
      </div>
    </section>
  );

  return (
    <section data-testid="playtest-board" style={styles.board}>
      <h4>Battlefield</h4>
      <PlaytestDropZone
        testId="battlefield"
        target={{ kind: 'zone', zone: 'battlefield' }}
        drops={drops}
        style={styles.battlefield}
      >
        {sections.map(renderSection)}
        {state.zones.battlefield.length === 0 ? <span>Battlefield is empty.</span> : null}
      </PlaytestDropZone>
    </section>
  );
};

PlaytestBoard.propTypes = {
  collectionId: PropTypes.string.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    details: PropTypes.instanceOf(Map).isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  drops: PropTypes.shape({
    canDrop: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
  }).isRequired,
};

export default PlaytestBoard;
